import {
    type PaidSubscriptionTier,
    SUBSCRIPTION_INTERVALS,
    SUBSCRIPTION_TIERS,
    type SubscriptionInterval,
} from '@/lib/api/subscriptions';
import { CHANGE_TYPE_LABELS } from '../constants';

/**
 * Determines if the user is upgrading or downgrading
 */
export function getChangeDirection(
    currentTier: PaidSubscriptionTier,
    newTier: PaidSubscriptionTier
): 'upgrade' | 'downgrade' | 'same' {
    if (currentTier === newTier) return 'same';
    return newTier === SUBSCRIPTION_TIERS.CREATOR_PRO ? 'upgrade' : 'downgrade';
}

/**
 * Generates the button text based on subscription changes
 */
export function getButtonText(params: {
    currentTier: PaidSubscriptionTier;
    newTier: PaidSubscriptionTier;
    currentInterval: SubscriptionInterval;
    newInterval: SubscriptionInterval;
    isCancelled: boolean;
}): string {
    const { currentTier, newTier, currentInterval, newInterval, isCancelled } = params;

    // If no changes and not cancelled, show current plan
    if (currentTier === newTier && currentInterval === newInterval && !isCancelled) {
        return 'Current Plan';
    }

    // If cancelled and no changes, just renew
    if (currentTier === newTier && currentInterval === newInterval && isCancelled) {
        return CHANGE_TYPE_LABELS.RENEW;
    }

    const intervalText = newInterval === SUBSCRIPTION_INTERVALS.YEAR ? 'Annual' : 'Monthly';
    const changeDirection = getChangeDirection(currentTier, newTier);

    let text = '';

    // Tier change
    if (currentTier !== newTier) {
        const directionLabel =
            changeDirection === 'upgrade' ? CHANGE_TYPE_LABELS.UPGRADE : CHANGE_TYPE_LABELS.DOWNGRADE;

        if (currentInterval !== newInterval) {
            text = `${directionLabel} and switch to ${intervalText}`;
        } else {
            text = `${directionLabel} plan`;
        }
    }
    // Only interval change
    else if (newInterval === SUBSCRIPTION_INTERVALS.MONTH) {
        text = CHANGE_TYPE_LABELS.SWITCH_TO_MONTHLY;
    } else {
        text = CHANGE_TYPE_LABELS.SWITCH_TO_YEARLY;
    }

    // Prepend renew if cancelled
    if (isCancelled) {
        text = `${CHANGE_TYPE_LABELS.RENEW} + ${text}`;
    }

    return text;
}

/**
 * Calculates the monthly display price based on interval
 */
export function calculateMonthlyDisplayPrice(price: number, interval: SubscriptionInterval): number {
    return interval === SUBSCRIPTION_INTERVALS.YEAR ? price / 12 : price;
}

/**
 * Formats the estimate message based on status
 */
export function getEstimateMessage(
    status: 'upgrade' | 'downgrade' | 'interval_change',
    cost: string,
    date: string
): string {
    const action = status === 'upgrade' ? 'You will be charged' : 'You will receive a credit of';
    return `${action} ${cost} on ${date}`;
}
